/**
 * AdminNoteMode — drop-in "Notes / Tickets" overlay for the Washy admin SPA.
 * ---------------------------------------------------------------------------
 * Port of the customer-site NoteMode (washy_website_ecommerce) so reviewers can
 * flag issues directly from the admin and file them into the SAME platform:
 *   https://admin-laravel.washywash.com/notes
 *
 * Tickets opened from here are tagged  source = "admin"  (Hashim's queue);
 * tickets from the website are  source = "website"  (Nizar's queue). The
 * platform has a Source filter to separate the two.
 *
 * DESIGN GOALS — add it WITHOUT touching anything in the admin:
 *   • Self-contained: the ONLY external deps are react + react-dom (already
 *     installed). No lucide, no react-router, no app context — icons are inline
 *     SVG and navigation is tracked by listening to history directly.
 *   • Self-mounting: importing this file is enough. It creates its own DOM node
 *     on <body> and renders into its own React root, so it never appears in the
 *     app's component tree and cannot affect existing pages, state or styles.
 *
 * INSTALL (one line — DO NOT add until told):
 *     import "./AdminNoteMode";          // in src/index.js
 *
 * REMOVE: delete that one import line (and optionally this file). Nothing else
 * in the codebase references it.
 *
 * Mode persists in localStorage ('ww_admin_note_mode') and is fully removable.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { createRoot } from "react-dom/client";

const PLATFORM = "https://admin-laravel.washywash.com/notes";
const SOURCE = "admin"; // every ticket from the admin is tagged for Hashim's queue
const LS_KEY = "ww_admin_note_mode";

/* ---- inline icons (no icon library dependency) ---- */
const Ico = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const IStickyNote = (p) => <Ico {...p} d={<><path d="M15.5 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9.5L21 14.5V5a2 2 0 0 0-2-2Z" /><path d="M15 21v-6a1 1 0 0 1 1-1h5" /></>} />;
const ICrosshair = (p) => <Ico {...p} d={<><circle cx="12" cy="12" r="9" /><line x1="22" y1="12" x2="18" y2="12" /><line x1="6" y1="12" x2="2" y2="12" /><line x1="12" y1="6" x2="12" y2="2" /><line x1="12" y1="22" x2="12" y2="18" /></>} />;
const IPlus = (p) => <Ico {...p} d={<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>} />;
const IX = (p) => <Ico {...p} d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />;
const IPower = (p) => <Ico {...p} d={<><path d="M18.36 6.64a9 9 0 1 1-12.73 0" /><line x1="12" y1="2" x2="12" y2="12" /></>} />;

/** Build a reasonably-unique CSS selector path for an element. */
function cssPath(el) {
  if (!(el instanceof Element)) return "";
  const parts = [];
  while (el && el.nodeType === 1 && el !== document.body && parts.length < 6) {
    let sel = el.nodeName.toLowerCase();
    if (el.id) { parts.unshift(`#${CSS.escape(el.id)}`); break; }
    const parent = el.parentNode;
    if (parent) {
      const siblings = Array.from(parent.children).filter((c) => c.nodeName === el.nodeName);
      if (siblings.length > 1) sel += `:nth-of-type(${siblings.indexOf(el) + 1})`;
    }
    parts.unshift(sel);
    el = el.parentNode;
  }
  return parts.join(" > ");
}

/** Find a human-friendly label for the section an element belongs to. */
function sectionLabel(el) {
  let node = el;
  while (node && node !== document.body) {
    if (node.dataset && node.dataset.section) return node.dataset.section;
    if (node.getAttribute && node.getAttribute("aria-label")) return node.getAttribute("aria-label");
    node = node.parentElement;
  }
  const container = el.closest("section, header, footer, main, article, .card, .panel") || el.parentElement;
  if (container) {
    const h = container.querySelector("h1, h2, h3, .card-title, .panel-title");
    if (h && h.textContent.trim()) return h.textContent.trim().slice(0, 120);
  }
  const txt = (el.textContent || "").trim();
  return txt ? txt.slice(0, 80) : el.nodeName.toLowerCase();
}

/**
 * Lightweight location signal — admin uses react-router (BrowserRouter) which
 * navigates via history.pushState without a full reload. We patch pushState/
 * replaceState (once) and listen to popstate to re-run the focus effect on
 * route changes, WITHOUT importing react-router here.
 */
function useLocationKey() {
  const [key, setKey] = useState(() => (typeof window !== "undefined" ? window.location.href : ""));
  useEffect(() => {
    const fire = () => setKey(window.location.href);
    if (!window.__wwNoteHistoryPatched) {
      window.__wwNoteHistoryPatched = true;
      ["pushState", "replaceState"].forEach((m) => {
        const orig = window.history[m];
        window.history[m] = function () {
          const r = orig.apply(this, arguments);
          window.dispatchEvent(new Event("ww-locationchange"));
          return r;
        };
      });
    }
    window.addEventListener("popstate", fire);
    window.addEventListener("ww-locationchange", fire);
    return () => {
      window.removeEventListener("popstate", fire);
      window.removeEventListener("ww-locationchange", fire);
    };
  }, []);
  return key;
}

function AdminNoteMode() {
  const locationKey = useLocationKey();

  // Show on admin hosts (admin.washywash.com) and local dev. Never elsewhere.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const enabled = /admin/i.test(host) || host === "localhost" || host.startsWith("127.") || host.startsWith("192.168.");

  const [on, setOn] = useState(() => localStorage.getItem(LS_KEY) === "1");
  const [picking, setPicking] = useState(false);
  const [sent, setSent] = useState(false);
  const hoverRef = useRef(null);
  // Linkback mode: the platform opened us with ?note_pick=1 to grab an element link.
  const linkbackRef = useRef(typeof window !== "undefined" && new URLSearchParams(window.location.search).get("note_pick") === "1");

  useEffect(() => {
    localStorage.setItem(LS_KEY, on ? "1" : "0");
    if (!on) setPicking(false);
  }, [on]);

  useEffect(() => {
    if (linkbackRef.current) { setOn(true); setPicking(true); }
  }, []);

  const buildDeepLink = (selector, section) => {
    const u = new URL(window.location.href);
    ["note_focus", "note_label", "new", "url", "note_pick"].forEach((k) => u.searchParams.delete(k));
    if (selector) u.searchParams.set("note_focus", selector);
    if (section) u.searchParams.set("note_label", section);
    return u.toString();
  };

  const currentUrl = () => {
    const u = new URL(window.location.href);
    ["note_focus", "note_label", "new", "url"].forEach((k) => u.searchParams.delete(k));
    return u.origin + u.pathname + (u.search ? u.search : "");
  };

  const openPlatform = useCallback((extra = {}) => {
    const p = new URLSearchParams({ new: "1", url: currentUrl(), source: SOURCE, ...extra });
    window.open(`${PLATFORM}?${p.toString()}`, "_blank", "noopener");
  }, []);

  /* ---- element picking ---- */
  useEffect(() => {
    if (!picking) return;

    const clear = () => { if (hoverRef.current) { hoverRef.current.style.outline = ""; hoverRef.current.style.cursor = ""; hoverRef.current = null; } };
    const isOurs = (t) => t.closest && t.closest("[data-notemode-ui]");

    const onMove = (e) => {
      const t = e.target;
      if (isOurs(t)) return;
      if (hoverRef.current && hoverRef.current !== t) clear();
      hoverRef.current = t;
      t.style.outline = "2px solid #4f46e5";
      t.style.outlineOffset = "1px";
      t.style.cursor = "crosshair";
    };
    const onClick = (e) => {
      const t = e.target;
      if (isOurs(t)) return;
      e.preventDefault(); e.stopPropagation();
      const selector = cssPath(t);
      const section = sectionLabel(t);
      clear(); setPicking(false);

      if (linkbackRef.current) {
        const url = buildDeepLink(selector, section);
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({ type: "ww-note-pick", url, selector, label: section }, "*");
          setSent(true);
          setTimeout(() => { try { window.close(); } catch { /* noop */ } }, 700);
        } else {
          try { navigator.clipboard.writeText(url); } catch { /* noop */ }
          setSent(true);
          setTimeout(() => setSent(false), 4000);
        }
        return;
      }
      openPlatform({ selector, section });
    };
    const onKey = (e) => { if (e.key === "Escape") { clear(); setPicking(false); } };

    document.addEventListener("mouseover", onMove, true);
    document.addEventListener("click", onClick, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      clear();
      document.removeEventListener("mouseover", onMove, true);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [picking, openPlatform]);

  /* ---- deep-link focus: ?note_focus=<selector>&note_label=<text> ---- */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const all = params.getAll("note_focus").filter(Boolean);
    const selector = all.length ? all[all.length - 1] : null;
    const label = (params.get("note_label") || "").trim();
    if (!selector && !label) return;

    let cancelled = false;
    let tries = 0;
    const MAX = 45; // ~9s at 200ms

    const bySelector = () => {
      if (!selector) return null;
      try { return document.querySelector(selector); } catch { return null; }
    };
    const byText = () => {
      const needle = label.slice(0, 60).toLowerCase();
      if (needle.length < 3) return null;
      const nodes = document.querySelectorAll("h1,h2,h3,h4,a,button,li,p,span,td,th,label");
      for (const e of nodes) {
        const tx = (e.textContent || "").trim().toLowerCase();
        if (tx && tx.includes(needle) && tx.length < needle.length + 140 && e.offsetParent !== null) return e;
      }
      return null;
    };

    const pulse = (el) => {
      const saved = { o: el.style.outline, off: el.style.outlineOffset, tr: el.style.transition };
      el.style.transition = "outline-color .35s";
      el.style.outline = "3px solid #4f46e5";
      el.style.outlineOffset = "3px";
      let n = 0;
      const id = setInterval(() => { el.style.outlineColor = n++ % 2 ? "#f59e0b" : "#4f46e5"; }, 450);
      setTimeout(() => {
        clearInterval(id);
        el.style.outline = saved.o; el.style.outlineOffset = saved.off; el.style.transition = saved.tr;
      }, 4500);
    };

    const lockTimers = [];
    const focusOn = (el) => {
      let userMoved = false;
      const mark = () => { userMoved = true; };
      const evs = ["wheel", "touchstart", "keydown", "mousedown"];
      evs.forEach((ev) => window.addEventListener(ev, mark, { passive: true }));

      const centre = (smooth) => el.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "center" });
      const reassert = () => {
        if (userMoved) return;
        const r = el.getBoundingClientRect();
        const c = r.top + r.height / 2;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (c < vh * 0.2 || c > vh * 0.8) centre(false);
      };

      centre(true);
      pulse(el);
      [250, 550, 950, 1500, 2300, 3200].forEach((ms) => lockTimers.push(setTimeout(reassert, ms)));
      lockTimers.push(setTimeout(() => evs.forEach((ev) => window.removeEventListener(ev, mark)), 3400));
    };

    const tick = () => {
      if (cancelled) return;
      const el = bySelector() || byText();
      if (el) { focusOn(el); return; }
      if (++tries < MAX) setTimeout(tick, 200);
    };
    const start = setTimeout(tick, 400);
    return () => { cancelled = true; clearTimeout(start); lockTimers.forEach(clearTimeout); };
  }, [locationKey]);

  /* ---- UI ---- */
  if (!enabled) return null;

  if (!on) {
    return (
      <button
        data-notemode-ui
        onClick={() => setOn(true)}
        title="Turn on Note mode — file an admin ticket"
        style={{
          position: "fixed", left: 16, bottom: 16, zIndex: 2147483000,
          display: "flex", alignItems: "center", gap: 7,
          background: "#0f172a", color: "#fff", border: 0, borderRadius: 999,
          padding: "9px 14px", fontWeight: 700, fontSize: 13,
          fontFamily: "Inter, system-ui, sans-serif",
          boxShadow: "0 8px 24px rgba(2,6,23,.35)", cursor: "pointer",
        }}
      >
        <IStickyNote size={16} /> Notes
      </button>
    );
  }

  return (
    <div data-notemode-ui style={{ position: "fixed", left: 16, bottom: 16, zIndex: 2147483000, fontFamily: "Inter, system-ui, sans-serif" }}>
      {picking && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, textAlign: "center", zIndex: 2147483000,
          background: linkbackRef.current ? "#0ea5e9" : "#4f46e5", color: "#fff", padding: "8px 12px", fontSize: 13, fontWeight: 600,
        }}>
          {linkbackRef.current
            ? "Click an element to copy its link back to your ticket · press Esc to cancel"
            : <>Click the element with the issue · press <b>Esc</b> to cancel</>}
        </div>
      )}
      {sent && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, textAlign: "center", zIndex: 2147483000,
          background: "#22c55e", color: "#fff", padding: "8px 12px", fontSize: 13, fontWeight: 700,
        }}>
          ✓ Element link captured — {window.opener && !window.opener.closed ? "added to your ticket comment." : "copied to clipboard, paste it in your comment."}
        </div>
      )}
      <div style={{
        background: "#fff", borderRadius: 16, boxShadow: "0 12px 32px rgba(2,6,23,.28)",
        border: "1px solid #e2e8f0", padding: 10, width: 210, color: "#0f172a",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: 999, background: "#22c55e" }} />
          <b style={{ fontSize: 13, color: "#0f172a" }}>Note mode on</b>
          <span style={{ fontSize: 10, fontWeight: 700, color: "#6d28d9", background: "#ede9fe", padding: "2px 6px", borderRadius: 999, textTransform: "uppercase", letterSpacing: ".3px" }}>Admin</span>
          <button onClick={() => setOn(false)} title="Turn off"
            style={{ marginLeft: "auto", background: "none", border: 0, color: "#94a3b8", cursor: "pointer", display: "flex" }}>
            <IPower size={16} />
          </button>
        </div>

        <button onClick={() => openPlatform()} style={btn("#4f46e5", "#fff")}>
          <IPlus size={15} /> Add note here
        </button>

        <button onClick={() => setPicking((p) => !p)}
          style={btn(picking ? "#ede9fe" : "#f1f5f9", picking ? "#4338ca" : "#0f172a", true)}>
          {picking ? <IX size={15} /> : <ICrosshair size={15} />} {picking ? "Cancel pick" : "Pick section"}
        </button>

        <a href={PLATFORM} target="_blank" rel="noopener noreferrer"
          style={{ display: "block", textAlign: "center", fontSize: 12, color: "#64748b", marginTop: 6 }}>
          Open notes platform ↗
        </a>
      </div>
    </div>
  );
}

const btn = (bg, color, mt) => ({
  width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
  background: bg, color, border: 0, borderRadius: 10, padding: "9px 12px",
  fontWeight: 700, fontSize: 13, cursor: "pointer", marginTop: mt ? 6 : 0,
});

/* ----------------------------------------------------------------------------
 * Self-mount. Importing this file renders the overlay into its own root on
 * <body>, isolated from the admin's React tree. Guarded against double-mount
 * (HMR / accidental double import).
 * -------------------------------------------------------------------------- */
function mount() {
  if (typeof document === "undefined") return;
  if (document.getElementById("ww-admin-note-root")) return; // already mounted
  const el = document.createElement("div");
  el.id = "ww-admin-note-root";
  document.body.appendChild(el);
  createRoot(el).render(<AdminNoteMode />);
}

if (typeof window !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", mount, { once: true });
  } else {
    mount();
  }
}

export default AdminNoteMode;
